<template>
  <!-- menu row: the language list expands inline, since a floating dropdown would be clipped by the scrollable modal -->
  <div v-if="inline" class="w-full" data-testid="locale-picker-inline">
    <a href="javascript:;" class="flex items-center justify-between font-['Inter'] py-2 w-full" @click="selectorExpanded = !selectorExpanded">
      <div class="flex flex-row items-center">
        <img src="@/assets/icons/translate.svg" class="w-4 h-4 mr-2" />
        <span class="text-white/90 not-italic font-normal">{{ $t('language') !== 'language' ? $t('language') : 'Language' }}</span>
      </div>
      <span class="flex items-center">
        <span class="text-[#8a8e92] not-italic font-normal mr-2">{{ selectedNativeName }}</span>
        <img src="@/assets/icons/arrow-right.svg" class="w-[20px] transition-transform" :class="{ 'rotate-90': selectorExpanded }" />
      </span>
    </a>
    <div v-if="selectorExpanded" class="pb-1">
      <span
          class="flex items-center cursor-pointer py-2 pl-6 hover:text-primary"
          :class="lang.code === normalizeCode(currentLocale) ? 'text-primary' : 'text-white/89'"
          v-for="lang in localizedLanguages"
          :key="lang.code"
          @click="setLocale(lang.code)"
      >
        <span class="inline-block w-4 h-4 mr-2">
          <img
              v-if="lang.code === normalizeCode(currentLocale)"
              src="@/assets/icons/sorenson/check.svg"
              alt="Selected"
              class="w-4 h-4"
          />
        </span>
        {{ lang.native_name }}
      </span>
    </div>
  </div>
  <div v-else class="relative inline-block" v-click-away="onClickAway">
    <a href="javascript:;" class="text-white/75 flex items-center gap-1" @click="toggleLocalesList">
      <img src="@/assets/icons/translate.svg" class="w-4 h-4 mr-1" />
      <span>{{ selectedNativeName }}</span>
    </a>
    <div
        class="absolute left-0 -ml-2 min-w-[120px] z-10 bg-[#171d24] rounded-[12px] py-2 shadow-lg bottom-[35px]"
        :class="selectorExpanded ? 'block' : 'hidden'"
    >
      <span
          class="flex items-center cursor-pointer px-4 py-2 hover:text-primary"
          :class="lang.code === normalizeCode(currentLocale) ? 'text-primary' : 'text-white/89'"
          v-for="lang in localizedLanguages"
          :key="lang.code"
          @click="setLocale(lang.code)"
      >
        <span class="flex items-center">
          <span class="inline-block w-4 h-4 mr-2">
            <img
                v-if="lang.code === normalizeCode(currentLocale)"
                src="@/assets/icons/sorenson/check.svg"
                alt="Selected"
                class="w-4 h-4"
            />
          </span>
          {{ lang.native_name }}
        </span>
      </span>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { loadLanguageAsync } from "@/plugins/i18n"
import { getPendo } from '@/services/pendo.service'

export default {
  name: 'LocalePicker',
  props: {
    initial: {
      type: String,
      required: true,
      default: "en"
    },
    // render as a settings-menu row instead of the footer dropdown
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectorExpanded: false,
    }
  },
  methods: {
    setLocale(locale) {
      if (locale === "pt") {
        locale = "pt-br"
      }

      loadLanguageAsync(locale)
      this.$i18n.locale = locale
      this.$store.dispatch("locale/set", locale)
      this.selectorExpanded = false

      const pendo = getPendo(this.user)
      if (pendo && this.user?.email) {
        pendo.identify({
          visitor: {
            id: this.user.email,
            forum_language: locale,
          },
          account: {}
        })
      }
    },

    toggleLocalesList() {
      this.selectorExpanded = !this.selectorExpanded
    },

    onClickAway() {
      this.selectorExpanded = false
    },

    normalizeCode(code) {
      return code ? code.split('-')[0].toLowerCase() : ''
    }
  },
  computed: {
    ...mapState('locale', ['locale', 'availableLocales']),
    ...mapState('nonpersist', ['user', 'prefetched']),
    ...mapState('lang', ['languages']),

    currentLocale() {
      return this.locale || this.initial
    },

    selectedNativeName() {
      const lang = this.localizedLanguages.find(
          l => l.code === this.normalizeCode(this.currentLocale)
      )
      return lang ? lang.native_name : this.currentLocale
    },

    localizedLanguages() {
      const availableCodes = Object.keys(this.availableLocales).map(this.normalizeCode)
      return this.languages.filter(lang =>
          availableCodes.includes(this.normalizeCode(lang.code))
      )
    }
  },

  created() {
    if (this.locale) return

    const browserLangs = navigator.languages
        ? navigator.languages.map(l => l.toLowerCase())
        : [navigator.language.toLowerCase()]

    const supportedCodes = this.localizedLanguages.map(l => this.normalizeCode(l.code))
    const matched = browserLangs.find(lang =>
        supportedCodes.includes(this.normalizeCode(lang))
    )

    if (matched) {
      this.setLocale(this.normalizeCode(matched))
    }
  },
}
</script>